import React, { useState } from 'react';
import { Grid, Typography } from '@mui/material';
import { Button } from '@tcl-boron-prefabs/button';
import { Form } from 'react-final-form';
import FormTextInput from '../../shared/form/FormTextInput';
import logger from '../../../../utils/logger';
import { trackEvent } from '../../../api/Pendo';
import { required } from '../../shared/form/validators';
import FormSingleDatePicker from '../../shared/form/FormSingleDatePicker';
import withPortalContainer from '../../hooks/withPortalContainer';
import usePortalContext from '../../context/usePortalContext';
import { useHistory } from 'react-router-dom';
import { NavigationPath } from '../../../api/constants';
import { Modal } from '@tcl-boron-prefabs/modal';
import { useAuth0 } from '@auth0/auth0-react';
import { IdentityVerificationRequest, VERIFICATION_DATE_FORMAT } from '@tempus/patient-web-service-shared';
import moment from 'moment';
import { Banner } from '@tcl-boron-prefabs/banner';
import { Fail } from '@tcl-argon-icons/icons';
import { useWindowDimensions } from '../../../api/WindowDimensions';
import { isMobileDevice } from '../../../utils';
import { createUseStyles } from 'react-jss';

const useModalStyle = createUseStyles({
  modalContent: {
    overflow: 'hidden',
  },
  modalContainerMobile: {
    maxWidth: '85vw',
    maxHeight: '70vh',
    padding: '5vh 5vw',
    boxSizing: 'border-box',
  },
});

export const sanitizeFormData = (data: IdentityVerificationRequest) => ({
  firstName: data.firstName?.trim(),
  lastName: data.lastName?.trim(),
  dateOfBirth: moment(data.dateOfBirth).format(VERIFICATION_DATE_FORMAT),
});

const IdentityVerification: React.FC<{ actionOverride?: () => Promise<void> }> = ({ actionOverride }) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isLockedOut, setIsLockedOut] = useState(false);
  const { push } = useHistory();
  const { fetchHelper } = usePortalContext();
  const { logout } = useAuth0();
  const windowDimensions = useWindowDimensions();
  const isMobile = isMobileDevice(windowDimensions);

  const handleLockRedirection = () => {
    logout({ logoutParams: { returnTo: window.location.origin } });
  };

  const submitForm = async (data: IdentityVerificationRequest) => {
    setLoading(true);
    setError(null);

    // Sanitize input data
    const sanitizedData = sanitizeFormData(data);

    try {
      await fetchHelper('/verification/identity', {
        method: 'POST',
        body: sanitizedData,
      });
      // If fetchHelper does not throw an error, then the verification was successful
      trackEvent('IdentityVerificationCompleted');
      if (actionOverride) {
        await actionOverride();
      } else {
        push(NavigationPath.HOME);
      }
    } catch (e: any) {
      const unexpectedError = 'An unexpected error occurred.';
      if (e && e.status) {
        if (e.status === 423) {
          trackEvent('IdentityVerificationFailed', { reason: 'Account locked' });
          setIsLockedOut(true);
        } else if (e.status === 401) {
          trackEvent('IdentityVerificationFailed', { reason: 'Authorization error', status: e.status });
          setError(e.httpMessage || 'An unexpected authorization error occurred.');
        } else {
          trackEvent('IdentityVerificationFailed', { reason: 'Server error', status: e.status });
          setError(e.httpMessage || unexpectedError);
          logger.error(e as Error, `An unexpected ${e.status} error occurred.`);
        }
      } else {
        trackEvent('IdentityVerificationFailed', { reason: 'Unknown error' });
        setError(e?.message || unexpectedError);
        logger.error(e as Error, 'An unexpected error occurred without a status code.');
      }
    } finally {
      setLoading(false);
    }
  };

  const classes = useModalStyle();

  return (
    <Grid container direction="column" flexGrow={1}>
      <Typography variant="h2" align="center" padding="20px 0">
        Verify your identity
      </Typography>
      <Typography variant="subtitle2">Please enter your information to verify your identity.</Typography>
      <Form
        onSubmit={submitForm}
        render={({ handleSubmit, invalid }) => (
          <Grid container direction="column" gap="20px" padding="20px">
            <FormTextInput
              label="First Name*"
              name="firstName"
              validate={required}
              data-testid="first-name"
              data-pendo="identity-verification-first-name"
            />
            <FormTextInput
              label="Last Name*"
              name="lastName"
              validate={required}
              data-testid="last-name"
              data-pendo="identity-verification-last-name"
            />
            <FormSingleDatePicker
              label="Date of Birth*"
              name="dateOfBirth"
              validate={required}
              data-testid="date-of-birth-picker"
              data-pendo="identity-verification-dob"
            />
            <Button
              ariaLabel="verify button"
              onClick={handleSubmit}
              loading={loading}
              disabled={invalid || isLockedOut}
              data-pendo="identity-verification-submit-button"
            >
              <Typography variant="button">Verify and Continue</Typography>
            </Button>
            {error && <Banner ariaLabel="warningBanner" status="error" message={error} Icon={Fail} />}
          </Grid>
        )}
      />
      <Modal
        isOpen={isLockedOut}
        title="Account Locked"
        size="medium"
        className={isMobile ? classes.modalContainerMobile : undefined}
        classes={{ content: classes.modalContent }}
        callToActionButton={{
          buttonText: 'Logout',
          id: 'Logout',
          onClick: () => handleLockRedirection(),
        }}
      >
        <Typography variant="body1">
          Your account has been locked due to multiple failed verification attempts. Please email{' '}
          <a href="mailto:help@tempus.com">help@tempus.com</a> or call (312) 598-9961 for assistance.
        </Typography>
      </Modal>
    </Grid>
  );
};

export default withPortalContainer(IdentityVerification);

export const OnboardingIdentityVerification: React.FC<{ next?: () => Promise<void> }> = ({ next }) => (
  <IdentityVerification actionOverride={next} />
);
